
import { LockDec } from '@/decorators'
import { Component, Vue } from 'vue-facing-decorator'

/**
 * 可以按门店勾选的通知事件。对应后端 ADMIN_NOTIFY_EVENT
 * （background-code-node/src/common/contants/index.contants.ts）：
 * 1 订单付款成功 2 买家提交退款申请 3 虚拟商品发货失败。
 *
 * 库里 notifyEvents 存的就是这几个数字，**只能往后追加，不能改已有的值** ——
 * 改值等于把所有人已经勾好的订阅悄悄换成别的事件。
 * */
const EVENT_LIST = [
  { value: 1, name: '订单付款成功' },
  { value: 2, name: '买家提交退款申请' },
  { value: 3, name: '虚拟商品发货失败' },
];

/**
 * 各渠道的地址前缀，和后端 notify.service.ts 的 NOTIFY_CHANNEL_HOST 必须一字不差。
 * 前端先拦一次只是为了少跑一趟接口；报错措辞也照抄后端，
 * 用户前后看到的是同一句话才不会以为是两个毛病。
 * */
const CHANNEL_PREFIX = {
  1: 'https://oapi.dingtalk.com/robot/send',
  2: 'https://qyapi.weixin.qq.com/cgi-bin/webhook/send',
};

const CHANNEL_LIST = [
  { name: '不推送', desc: '地址保留', value: 0 },
  { name: '钉钉', desc: '群机器人', value: 1 },
  { name: '企业微信', desc: '群机器人', value: 2 },
];

/** 后端 set-notify 的原话 */
const CHANNEL_MISMATCH = '通知地址和渠道对不上，请确认复制的是对应平台的群机器人地址';

/**
 * 「怎么找到这个地址」的步骤。
 *
 * 钉钉那段的关键词建议是**照着后端实际推的文案**写的，改后端推送内容时要回来对一遍
 * （背景：钉钉的安全设置三种必须选一种，选「自定义关键词」时消息里没有关键词会被直接拒收）：
 *   付款     【新订单】买家已付款 / 订单号 金额 商品
 *   退款申请 【退款申请】…待审批 / 订单号 退款金额 申请原因
 *   发货失败 【虚拟商品发货失败】/ 订单号 失败原因 已重试
 *   测试     【通知测试】/ 账号 / 收到这条消息说明通知地址配置正确
 * 四条共有的词只有「订单号」「订单」，而测试消息里**没有**「订单」，所以两个关键词都要填 ——
 * 只填「订单」的话业务通知能到、点「保存并测试」却会失败，最难排查的那一种。
 * */
const HELP_LIST = {
  1: {
    name: '钉钉',
    steps: [
      '在要接收通知的钉钉群里点右上角「群设置」，进「智能群助手」→「添加机器人」→ 选「自定义」',
      '安全设置必须选一种：勾「自定义关键词」，关键词至少填「订单」和「通知测试」两个 —— 消息里没有关键词会被钉钉直接拒收，而测试消息里只有「通知测试」，只填「订单」的话测试会失败',
      '不要选「加签」：当前推送不带签名，选了以后一条都发不出去。也不想填关键词就用「IP 地址段」，但要填服务器的出口 IP',
      '建好后复制 Webhook 地址，粘到上面的输入框',
    ],
    doc: 'https://open.dingtalk.com/document/robots/customize-robot-security-settings',
    docName: '钉钉官方：自定义机器人安全设置',
  },
  2: {
    name: '企业微信',
    steps: [
      '在要接收通知的企业微信群里点右上角「…」→「群机器人」→「添加机器人」→「新创建一个机器人」',
      '起个名字，点「添加机器人」',
      '复制 Webhook 地址，粘到上面的输入框',
      '群机器人没有关键词/加签之类的安全设置，但地址里的 key 本身就是凭证，别外传',
    ],
    doc: 'https://developer.work.weixin.qq.com/document/path/91770',
    docName: '企业微信官方：群机器人配置说明',
  },
};

@Component({})
export default class extends Vue {
  loading = false;
  saveLoading = false;
  testLoading = false;
  eventList = EVENT_LIST;
  channelList = CHANNEL_LIST;
  pageNo = 1;
  pageSize = 10;
  /** 获取步骤默认收起，但没配过地址的人（见 loadNotify）一进来就展开 */
  showHelp = false;
  form = {
    notifyType: 0,
    notifyUrl: ''
  };
  /**
   * 每个门店一行：
   *   storeId / storeName 展示用
   *   events   勾中的事件，渲染只认它（三态里的 null 进这里已经展开成全部）
   *   extra    后端以后新增、前端还不认识的事件，保存时原样带回去，别丢
   *   origin   进弹窗时的原始值，只用来判断这行改没改，不参与渲染
   * */
  storeRows: any[] = [];

  get allValues() {
    return this.eventList.map((item) => item.value);
  }

  get saving() {
    return this.saveLoading || this.testLoading;
  }

  get urlPlaceholder() {
    return this.form.notifyType == 0 ? '选「不推送」时可以留空' : '请粘贴群机器人 webhook 地址';
  }

  get urlTips() {
    let prefix = CHANNEL_PREFIX[Number(this.form.notifyType)];
    if (!prefix) {
      return '填了地址也可以先「保存并测试」，确认通了再开推送 —— 测试只认地址，不看这里选了什么。';
    }
    return `地址必须以 ${prefix} 开头。`;
  }

  /** 选好渠道才给对应的那份；「不推送」下不显示 —— 都不推了，去哪找地址就不是当下要回答的问题 */
  get helpList() {
    let list = HELP_LIST[Number(this.form.notifyType)];
    return list ? [list] : [];
  }

  get pageInfo() {
    return { countTotal: this.storeRows.length, pageSize: this.pageSize };
  }

  /** 门店可能很多，表格只渲染当前这一页 —— 顺带把弹窗高度也压住了 */
  get pageRows() {
    let start = (this.pageNo - 1) * this.pageSize;
    return this.storeRows.slice(start, start + this.pageSize);
  }

  /** 全选只认当前页：分页了还要跨页全选的话，用户根本看不到自己选了什么 */
  get allRowsChecked() {
    return this.pageRows.length > 0 && this.pageRows.every((row) => this.isRowAll(row));
  }

  get allRowsIndeterminate() {
    return !this.allRowsChecked && this.pageRows.some((row) => row.events.length > 0);
  }

  /** 只提交改过的行，免得开一次弹窗点保存就发 N 个请求 */
  get changedRows() {
    return this.storeRows.filter((row) => {
      /** origin 是 null 就还是 null，是 [] 就还是 []，JSON.stringify 正好能区分这两者 */
      let origin = row.origin == null ? null : [...row.origin].sort((a, b) => a - b);
      return JSON.stringify(this.payloadEvents(row)) !== JSON.stringify(origin);
    });
  }

  async created() {
    this.loading = true;
    await Promise.all([this.loadNotify(), this.loadStoreList()]);
    this.loading = false;
  }

  async loadNotify() {
    let { error, data, message } = await this.$post('/manager/common/get-notify');
    if (error) return this.$error(message);
    this.form.notifyType = Number(data?.notifyType || 0);
    this.form.notifyUrl = data?.notifyUrl || '';
    /** 还没配过地址的人一进来就展开步骤 —— 不给提示的话「去哪找这个地址」是整件事最大的坎 */
    this.showHelp = !this.form.notifyUrl;
  }

  async loadStoreList() {
    let { error, data, message } = await this.$post('/manager/common/notify-store-list');
    if (error) return this.$error(message);
    this.storeRows = (data || []).map((item) => {
      /** json 列逐项转数字：勾选判定是严格比较，字符串 "1" 会静默显示成没勾上 */
      let raw = (item.notifyEvents == null ? [] : item.notifyEvents).map((value) => Number(value));
      return {
        storeId: item.storeId,
        storeName: item.storeName,
        /**
         * null = 从没配过 = 全收，展开成「全部勾上」；[] = 显式关掉，保持空。
         * 两者绝不能折成同一个值，否则「没配过」会被显示成「一个都不收」
         * */
        events: item.notifyEvents == null ? this.allValues.slice() : raw.filter((value) => this.allValues.includes(value)),
        extra: raw.filter((value) => !this.allValues.includes(value)),
        origin: item.notifyEvents == null ? null : raw
      };
    });
  }

  isRowAll(row) {
    return this.allValues.every((value) => row.events.includes(value));
  }

  isRowIndeterminate(row) {
    return !this.isRowAll(row) && row.events.length > 0;
  }

  setEvent(row, value, checked) {
    let index = row.events.indexOf(value);
    if (checked && index == -1) {
      row.events.push(value);
    } else if (!checked && index >= 0) {
      row.events.splice(index, 1);
    }
  }

  toggleRow(row, checked) {
    row.events = checked ? this.allValues.slice() : [];
  }

  toggleAllRows(checked) {
    this.pageRows.forEach((row) => this.toggleRow(row, checked));
  }

  /**
   * 这一行要回传什么。
   *
   * 全部勾上时回 null（后端说的「默认全收」），而不是 [1,2,3]：事件枚举是只增不改的，
   * 回字面数组的话，将来后端加了事件 4，所有点过这个弹窗的人会**静默漏推** ——
   * 而界面上这一行看起来还是全勾，没人会发现。null 会自动覆盖以后新增的事件。
   *
   * 代价是丢掉「我特意只选了这三个」这个区分，但它今天不可观测
   * （三个里选三个，两种写法行为一样），不值得拿它换漏推。
   * */
  payloadEvents(row) {
    if (this.isRowAll(row) && !row.extra.length) return null;
    return [...row.events, ...row.extra].sort((a, b) => a - b);
  }

  /** 镜像后端 set-notify 的校验顺序，把失败收成一条错误，而不是「保存成功」+「发送失败」两条 */
  checkUrl() {
    let url = `${this.form.notifyUrl || ''}`.trim();
    let prefix = CHANNEL_PREFIX[Number(this.form.notifyType)];
    if (prefix) {
      if (!url) return '请填写通知地址';
      if (url.indexOf(prefix) != 0) return CHANNEL_MISMATCH;
      return '';
    }
    /** 不推送时后端不校验地址，但地址留着以后要测，先在这儿说清楚 */
    if (url && !this.matchAnyChannel(url)) {
      return '这个地址不是钉钉/企业微信的群机器人地址，保存后无法测试';
    }
    return '';
  }

  matchAnyChannel(url) {
    return Object.keys(CHANNEL_PREFIX).some((key) => url.indexOf(CHANNEL_PREFIX[key]) == 0);
  }

  @LockDec('saveLoading')
  save() {
    return this.submit(false);
  }

  @LockDec('testLoading')
  saveAndTest() {
    return this.submit(true);
  }

  async submit(test: boolean) {
    let checkError = this.checkUrl();
    if (checkError) return this.$error(checkError);

    let { error, message } = await this.$post('/manager/common/set-notify', {
      notifyType: Number(this.form.notifyType),
      /** 不推送也照传：后端会把地址原样存下，清空就再也测不了了 */
      notifyUrl: `${this.form.notifyUrl || ''}`.trim()
    });
    if (error) return this.$error(message);

    for (let row of this.changedRows) {
      let res = await this.$post('/manager/common/set-notify-store', {
        storeId: row.storeId,
        /** null = 恢复默认全收，[] = 完全不推，后端靠这个区分 */
        events: this.payloadEvents(row)
      });
      /** 中途失败就停：已经存下的正是用户要的，重进弹窗再补剩下的 */
      if (res.error) return this.$error(res.message);
      /** 存过就得更新基准，否则再点一次「保存并测试」会把同一批值重发一遍 */
      row.origin = this.payloadEvents(row);
    }

    if (!test) {
      this.$success('保存成功');
      return this.$emit('resolve');
    }

    /**
     * 测试打的是**库里**的地址，所以必须先落库再测，否则测的是上一个地址。
     * 保存这时已经成功了，失败就不关弹窗，让用户改完地址直接重测
     * */
    let { error: testError, message: testMessage } = await this.$post('/manager/common/test-notify');
    if (testError) return this.$error(testMessage);
    this.$success('已发送测试消息，请到群里确认');
  }

  close() {
    this.$emit('reject', { msg: '取消' });
  }
}
